import { Action, State, StateContext } from '@ngxs/store';
import {
  RegisterPaymentRequestInfo,
  PurgeAvailablePaymentMethods,
  RegisterBankIdSignatureStatus,
  ResetBankIdSignatureStatus,
} from './payment-request.actions';
import {
  PaymentRequestDto,
  AuthorizedPaymentMethod,
} from '../../model/payment-request/payment-request.dto';
import { BankIdSignatureStatusDto } from '../../model/payment-request/bank-id-signature-status.dto';
import { Injectable } from '@angular/core';

export class PaymentRequestStateModel {
  loaded: boolean;
  bankIdStatus: BankIdSignatureStatusDto;
  errors: string[];
  request: PaymentRequestDto;
}

@State<PaymentRequestStateModel>({
  name: 'paymentRequest',
  defaults: {
    loaded: false,
    bankIdStatus: null,
    errors: [],
    request: {
      id: '',
      status: '',
      value: null,
      amount: 0,
      currency: null,
      market: null,
      language: null,
      amountDenotesCreditLimit: false,
      lowestPossibleMonthlyPayment: 0,
      title: '',
      description: '',
      merchantDetails: {
        companyName: '',
        logoUrl: '',
      },
      availableMethods: [],
      settlementMethod: null,
      phoneNumber: null,
      paymentChannel: null,
      lineItems: null,
      cancelUrl: '',
      requireFurtherAIS: false,
    },
  },
})
@Injectable()
export class PaymentRequestState {
  @Action(RegisterBankIdSignatureStatus)
  registerBankIdSignatureStatus(
    ctx: StateContext<PaymentRequestStateModel>,
    action: RegisterBankIdSignatureStatus
  ) {
    const { patchState, getState } = ctx;

    const state = getState();

    if (!action.statusDto.autoStartToken && state.bankIdStatus) {
      action.statusDto.autoStartToken = state.bankIdStatus.autoStartToken;
    }

    patchState({
      ...state,
      bankIdStatus: action.statusDto,
    });
  }

  @Action(ResetBankIdSignatureStatus)
  resetBankIdSignatureStatus(
    ctx: StateContext<PaymentRequestStateModel>,
    action: ResetBankIdSignatureStatus
  ) {
    const { patchState, getState } = ctx;

    const state = getState();

    patchState({
      ...state,
      bankIdStatus: null,
    });
  }

  @Action(RegisterPaymentRequestInfo)
  getPaymentRequestInfo(
    ctx: StateContext<PaymentRequestStateModel>,
    action: RegisterPaymentRequestInfo
  ) {
    const { patchState, getState } = ctx;

    const currentId = getState().request.id;

    if (currentId !== action.payload.paymentRequest.id) {
      patchState({
        ...getState(),
        request: {
          id: '',
          status: '',
          value: null,
          amount: 0,
          currency: null,
          market: null,
          language: null,
          amountDenotesCreditLimit: false,
          lowestPossibleMonthlyPayment: 0,
          title: '',
          description: '',
          merchantDetails: {
            companyName: '',
            logoUrl: '',
          },
          availableMethods: [],
          settlementMethod: null,
          phoneNumber: null,
          paymentChannel: null,
          lineItems: null,
          cancelUrl: '',
          requireFurtherAIS: false,
        },
      });
    }

    patchState({
      loaded: true,
      request: action.payload.paymentRequest,
    });
  }

  @Action(PurgeAvailablePaymentMethods)
  purgeAvailablePaymentMethods(
    ctx: StateContext<PaymentRequestStateModel>,
    action: PurgeAvailablePaymentMethods
  ) {
    const { getState, patchState } = ctx;

    const available: AuthorizedPaymentMethod[] =
      getState().request.availableMethods.filter((methodType) => {
        return !action.paymentMethodIdentifiers.includes(
          methodType.type.toString()
        );
      });

    patchState({
      ...getState(),
      request: {
        ...getState().request,
        availableMethods: available,
      },
    });
  }
}
